import { useState, useEffect, useMemo, type ChangeEvent } from "react";
import { useLocalization } from "@fluent/react";
import {
  Alert,
  Avatar,
  Button,
  ContentCard,
  Drawer,
  MenuButton,
  Row,
  Select,
  TextInput,
} from "@narmi/design_system";
import {
  useNotificationContext,
  PhoneTextInput,
  phoneFormatter,
  ContextForm,
  Dialog,
} from "cerulean";
import OrganizationUser, {
  parseFieldErrors,
} from "byzantine/src/OrganizationUser";
import { useRolesContext } from "byzantine/src/Role";
import { useNavigate } from "react-router-dom";
import { useOrganizationUserContext } from "byzantine/src/OrganizationUser/OrganizationUserProvider";
import OrganizationUserStatusChip from "./OrganizationUserStatusChip";
import styles from "./OrganizationUserInfoDrawer.module.scss";

interface OrganizationUserInfoDrawerProps {
  isOpen: boolean;
  selectedUser: OrganizationUser | null;
  onUserDismiss: () => void;
  currentOrganizationUser: OrganizationUser | null;
  onNext?: () => void;
  onPrev?: () => void;
}

export function OrganizationUserInfoDrawer({
  isOpen,
  selectedUser,
  onUserDismiss,
  currentOrganizationUser,
  onNext,
  onPrev,
}: OrganizationUserInfoDrawerProps) {
  const { l10n } = useLocalization();
  const { sendNotification } = useNotificationContext();
  const {
    assignableRoles,
    isLoading: rolesLoading,
    error: rolesError,
  } = useRolesContext();
  const {refresh, updateOrganizationUser} = useOrganizationUserContext();
  const navigate = useNavigate();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [phone, setPhone] = useState("");
  const [role, setRole] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [originalFirstName, setOriginalFirstName] = useState("");
  const [originalLastName, setOriginalLastName] = useState("");
  const [originalRole, setOriginalRole] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [isDeactivateModalOpen, setIsDeactivateModalOpen] = useState(false);

  const currentRole = selectedUser?.role;

  // Combine assignable roles with the user's current role if it's not assignable
  const roleOptions = useMemo(() => {
    const userCurrentRole = selectedUser?.role;
    const isCurrentRoleAssignable = assignableRoles.some(
      (r) => r.uuid === userCurrentRole?.uuid
    );

    // If current role is already in assignable roles, just use assignable roles
    if (isCurrentRoleAssignable || !userCurrentRole) {
      return assignableRoles;
    }

    // Otherwise, add the current role to the list
    return [...assignableRoles, userCurrentRole];
  }, [assignableRoles, currentRole]);

  useEffect(() => {
    if (selectedUser) {
      setFirstName(selectedUser.user.first_name);
      setLastName(selectedUser.user.last_name);
      setEmail(selectedUser.user.email);
      setUsername(selectedUser.user.username);
      const phoneValue =
        typeof selectedUser.user.phone === "string"
          ? selectedUser.user.phone
          : selectedUser.user.phone?.number || "";
      setPhone(phoneFormatter(phoneValue));
      setRole(selectedUser.role.uuid || "");

      // Set original values for change detection
      setOriginalFirstName(selectedUser.user.first_name);
      setOriginalLastName(selectedUser.user.last_name);
      setOriginalRole(selectedUser.role.uuid || "");

      // Clear any previous errors
      setError(null);
      setFieldErrors({});
    }
  }, [selectedUser]);

  useEffect(() => {
    // Clear field errors when user edits any of the input fields
    setFieldErrors({});
  }, [firstName, lastName, role]);

  useEffect(() => {
    if (!isOpen || !selectedUser) {
      setIsDeactivateModalOpen(false);
    }
  }, [isOpen, selectedUser]);

  const handleSaveChanges = async () => {
    if (!selectedUser) return;

    setIsLoading(true);
    setError(null);
    setFieldErrors({});

    try {
      const payload: API.UpdateOrganizationUser.Request = {
        first_name: firstName,
        last_name: lastName,
        role_uuid: role,
      };

      await updateOrganizationUser({
        uuid: selectedUser.uuid,
        payload,
      });

      sendNotification({
        type: "success",
        text: l10n.getString("org-user-info-success"),
      });

      refresh();
      onUserDismiss();
    } catch (err) {
      const errors = parseFieldErrors(err);
      setFieldErrors(errors);

      // If no field-specific errors, show generic error
      if (Object.keys(errors).length === 0) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : l10n.getString("org-user-save-failed");
        setError(errorMessage);
      } else {
        setError(null); // Clear generic error if we have field errors
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeactivateClick = async (callback: (error?: any) => void) => {
    if (!selectedUser) {
      callback();
      return;
    }

    try {
      await selectedUser.deactivate();
      setIsDeactivateModalOpen(false);
      callback();
      sendNotification({
        type: "success",
        text: l10n.getString("org-user-deactivate-success"),
      });
      // Parent will close drawer and clear URL in handleUserUpdated
      refresh();
      onUserDismiss();
    } catch (err) {
      callback(err);
    }
  };

  const isEditable =
    !selectedUser || !currentOrganizationUser
      ? true
      : !selectedUser.reasonIfNotEditable(
          currentOrganizationUser.role.name || "",
          currentOrganizationUser.uuid || ""
        );
  const selectedUserStatus = selectedUser?.status || "";

  const hasChanges =
    firstName !== originalFirstName ||
    lastName !== originalLastName ||
    role !== originalRole;

  const submitButtonCopy = (userStatus: string) => {
    if (userStatus === OrganizationUser.STATUS.INACTIVE) {
      return l10n.getString("org-user-button-reactivate");
    } else if (userStatus === OrganizationUser.STATUS.PENDING) {
      return l10n.getString("org-user-button-resend-invite");
    } else {
      return l10n.getString("org-user-button-save-changes");
    }
  };

  const drawerFooter = () => (
    <div className={styles.buttonContainer}>
      <Row gapSize="xs" alignItems="center" justifyContent="end">
        <Row.Item shrink>
          <Button
            onClick={handleSaveChanges}
            disabled={
              isLoading ||
              !selectedUser ||
              !isEditable ||
              (!hasChanges &&
                selectedUserStatus === OrganizationUser.STATUS.ACTIVE)
            }
          >
            {submitButtonCopy(selectedUserStatus)}
          </Button>
        </Row.Item>
        <Row.Item shrink>
           <Button
             kind="secondary"
             startIcon="x"
             onClick={onUserDismiss}
             disabled={isLoading}
           >
             {l10n.getString("org-user-button-close")}
           </Button>
        </Row.Item>
      </Row>
    </div>
  );

  return (
    <Drawer
      isOpen={isOpen}
      onUserDismiss={onUserDismiss}
      depth="524px"
      paddingSize="l"
      footer={drawerFooter()}
      onNext={onNext}
      onPrev={onPrev}
    >
      {selectedUser && (<div className={styles.drawerContent}>
        <div className={styles.contentWrapper}>
          <div className={styles.header}>
            <Row gapSize="s" alignItems="center">
              <Row.Item shrink>
                <Avatar
                  initials={selectedUser.userInitials()}
                  label={selectedUser.fullName()}
                />
              </Row.Item>
              <Row.Item>
                <div>
                  <div className="fontSize--m fontColor--primary">
                    {selectedUser.fullName()}
                  </div>
                  <p className="fontSize--s fontColor--secondary">
                    {selectedUser.user.email ?? ""}
                  </p>
                </div>
              </Row.Item>
              <Row.Item shrink>
                <OrganizationUserStatusChip status={selectedUser.status} />
              </Row.Item>
               {selectedUser.status !== OrganizationUser.STATUS.INACTIVE && isEditable && (
                 <Row.Item shrink>
                   <MenuButton>
                     <MenuButton.Item
                       label={l10n.getString("org-user-deactivate")}
                       onSelect={() => setIsDeactivateModalOpen(true)}
                     />
                   </MenuButton>
                 </Row.Item>
               )}
            </Row>
          </div>

          {error && (
            <div className={styles.errorMessage}>
              <div className={styles.errorIcon}>⚠</div>
              <div className={styles.errorText}>{error}</div>
            </div>
          )}

          <div className={styles.sectionTitle}>{l10n.getString("org-user-details-section")}</div>
          <ContentCard paddingSize="l" kind="bordered">
            <Row>
              <Row.Item>
                <div>
                  <div className={styles.fieldTitle}>
                    {l10n.getString("label-first-name")}
                  </div>
                   <TextInput
                     value={firstName}
                     onChange={(e: ChangeEvent<HTMLInputElement>) =>
                       setFirstName(e.target.value)
                     }
                     disabled={!isEditable}
                     error={fieldErrors.first_name}
                   />
                </div>
              </Row.Item>
              <Row.Item>
                <div>
                  <div className={styles.fieldTitle}>
                    {l10n.getString("label-last-name")}
                  </div>
                   <TextInput
                     value={lastName}
                     onChange={(e: ChangeEvent<HTMLInputElement>) =>
                       setLastName(e.target.value)
                     }
                     disabled={!isEditable}
                     error={fieldErrors.last_name}
                   />
                </div>
              </Row.Item>
            </Row>

            <div className={styles.fieldRow}>
              <div className={styles.fieldTitle}>
                {l10n.getString("label-email")}
              </div>
              <TextInput
                disabled={true}
                startIcon="mail"
                value={email}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setEmail(e.target.value)
                }
              />
            </div>

            <div className={styles.fieldRow}>
              <div className={styles.fieldTitle}>
                {l10n.getString("label-username")}
              </div>
              <TextInput
                disabled={true}
                value={username}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setUsername(e.target.value)
                }
              />
            </div>

            <div className={styles.fieldRow}>
              <div className={styles.fieldTitle}>
                {l10n.getString("label-phone-number")}
              </div>
              <PhoneTextInput
                disabled={true}
                value={phone}
                onChange={(value) => setPhone(value)}
                startIcon="phone"
              />
            </div>

              <div className={styles.fieldRow}>
                <div className={styles.fieldTitle}>
                  {l10n.getString("org-user-filter-role")}
                </div>
                <Select
                  value={role}
                  onChange={(value: string) => setRole(value)}
                  disabled={rolesLoading || !isEditable}
                >
                  {rolesLoading && (
                    <Select.Item value="">
                      {l10n.getString("org-user-role-loading")}
                    </Select.Item>
                  )}
                  {!rolesLoading && rolesError && (
                    <Select.Item value="">
                      {l10n.getString("org-user-role-error")}
                    </Select.Item>
                  )}
                  {!rolesLoading && !rolesError && roleOptions.length === 0 && (
                    <Select.Item value="">
                      {l10n.getString("org-user-role-empty")}
                    </Select.Item>
                  )}
                  {!rolesLoading &&
                    !rolesError &&
                    roleOptions.map((r) => (
                      <Select.Item key={r.uuid} value={r.uuid || ""}>
                        {r.getDisplayName()}
                      </Select.Item>
                    ))}
                </Select>
              </div>
              {currentRole?.isUnassigned() && (
                <div className="padding--top--l">
                  <Alert isDismissable={false} kind="warn" isActive={true}>
                    <p>{l10n.getString("org-user-unassigned-drawer-warning")}</p>
                    <Button
                      kind="plain"
                      endIcon={"arrow-right"}
                      onClick={() => {
                        navigate(`/roles/${currentRole.uuid}/duplicate`, {
                          state: {
                            redirectTo: {
                              type: "user",
                              userUuid: selectedUser.uuid,
                            },
                          },
                        });
                      }}
                    >
                      {l10n.getString("org-user-duplicate-button")}
                    </Button>
                  </Alert>
                </div>
              )}
            </ContentCard>
          </div>
        </div>
      )}

       {isDeactivateModalOpen && selectedUser && (
          <Dialog
           isOpen={isDeactivateModalOpen}
           onUserDismiss={() => setIsDeactivateModalOpen(false)}
           title={l10n.getString("org-user-deactivate")}
         >
           <ContextForm nativeForm={false}>
             <div className={styles.deactivateConfirmText}>
               {l10n.getString("org-user-deactivate-confirm", {
                 firstName: selectedUser.user.first_name,
                 lastName: selectedUser.user.last_name,
               })}
             </div>
             <Row justifyContent="end" alignItems="center" gapSize="m">
               <Row.Item shrink>
                 <Button
                   kind="negative"
                   type="button"
                   label={l10n.getString("org-user-deactivate-cancel")}
                   testId="cancel-deactivate-user-button"
                   onClick={() => setIsDeactivateModalOpen(false)}
                 />
               </Row.Item>
               <Row.Item shrink>
                 <ContextForm.Action onSubmit={handleDeactivateClick}>
                   <Button
                     kind="primary"
                     label={l10n.getString("org-user-deactivate-confirm-yes")}
                     testId="confirm-deactivate-user-button"
                   />
                 </ContextForm.Action>
               </Row.Item>
             </Row>
           </ContextForm>
         </Dialog>
        )}
    </Drawer>
  );
}

export default OrganizationUserInfoDrawer;
